import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import HighLogo from '../../components/HighLogo';
import logo from '../../../assets/logo.png';
import SmartVideoPlayerGallery from '../../../web/pages/Presentation/SmartVideoPlayerGallery';
import HashtagSelector from '../../../shared/components/HashtagSelectorMobile';
import Modal from "../../components/ModalReport";


interface SidebarData {
  hashtags?: string[];
}

type ExtMediaItem =
  | {
      id: string;
      kind: 'image';
      url: string;
    }
  | {
      id: string;
      kind: 'video';
      videoId?: string | null;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
      musicId?: string | null;
    };

const API_BASE = process.env.REACT_APP_API_BASE_URL!;
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const resolveVideoSrc = (item: Extract<ExtMediaItem, { kind: 'video' }>) => {
  if (item.hlsUrl) return item.hlsUrl;
  if (item.videoId) return `${API_ORIGIN}/hls/playlist/${item.videoId}`;
  return '';
};

// Accepts string or object from server and preserves music meta
const fromServerMedia = (m: any, index: number): ExtMediaItem => {
  // HLS video (correct path)
if (m?.isHls === true || m?.videoId || m?.hlsUrl || m?.dashUrl) {
  return {
    id: String(m?._id ?? m?.videoId ?? m?.hlsUrl ?? `video-${index}`),
    kind: 'video',
    videoId: m.videoId ?? null,
    hlsUrl: m.hlsUrl ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? null,
    musicTitle: m.musicTitle ?? m.music?.title ?? null,
    musicArtist: m.musicArtist ?? m.music?.artist ?? null,
    musicId: m.musicId ?? m.music?._id ?? null,
  };
}

  // image fallback
  const url =
    m?.url ??
    m?.image?.url ??
    m?.src ??
    m?.path ??
    '';

  return {
    id: String(m?._id ?? url ?? `img-${index}`),
    kind: 'image',
    url: String(url),
  };
};


const MAX_TAGS = 8;

function normalizeTag(raw: string) {
  const cleaned = raw.trim();
  if (!cleaned) return '';
  const withHash = cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
  return withHash.replace(/\s+/g, '').toLowerCase();
}

const GalleryEditPosting = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { postId: postIdFromParams } = useParams<{ postId: string }>();
  const { postId: postIdFromState } = (location.state || {}) as { postId?: string };
  const postId = postIdFromParams || postIdFromState;

  const [isLoading, setIsLoading] = useState(false);
  const [userEnteredHashtags, setUserEnteredHashtags] = useState('');
  const { user } = useAuth();
  const { sidebarData }: { sidebarData?: SidebarData } = (location.state || {}) as {
    sidebarData?: SidebarData;
  };


  const [existingMedia, setExistingMedia] = useState<ExtMediaItem[]>([]);
  const [selectedHashtagList, setSelectedHashtagList] = useState<string[]>([]);

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
const [cancelBusy, setCancelBusy] = useState(false);

const [successOpen, setSuccessOpen] = useState(false);

const isSaveDisabled = existingMedia.length === 0;
const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
const [saveBusy, setSaveBusy] = useState(false);


  const hasAvatar = Boolean(user?.picture);
  const ROW_TOP_OFFSET = hasAvatar ? 0 : 5; 

  // Apply sidebar hashtags only once (if provided)
  useEffect(() => {
    if (!sidebarData?.hashtags) return;
  
    const limited = sidebarData.hashtags.slice(0, MAX_TAGS);
    setSelectedHashtagList(limited);
    setUserEnteredHashtags(limited.join(' '));
  }, [sidebarData]);
  

  // Fetch existing media data if postId is provided
  useEffect(() => {
    const fetchMediaData = async () => {
      if (!postId) return;

      try {
        const res = await api.get(`/media/${postId}`);
        const post = res.data?.post ?? res.data ?? {};
        const rawList: any[] = Array.isArray(post.media)
          ? post.media
          : Array.isArray(post.mediaUrls)
          ? post.mediaUrls
          : [];

        const fetched = rawList.map(fromServerMedia) as ExtMediaItem[];
        setExistingMedia(fetched);

        if (Array.isArray(post.hashtags) && post.hashtags.length) {
          const displayTags = post.hashtags.map((h: string) =>
            h.startsWith('#') ? h : `#${h}`
          );
        
          const limitedTags = displayTags.slice(0, MAX_TAGS);
          setSelectedHashtagList(limitedTags);
          setUserEnteredHashtags(limitedTags.join(' '));
        }
        
      } catch (err) {
        console.error('Failed to fetch post media:', err);
      }
    };

    fetchMediaData();
  }, [postId]);

  const mediaLabel = (m?: { musicTitle?: string | null; musicArtist?: string | null }) =>
    [m?.musicTitle, m?.musicArtist].filter(Boolean).join(' — ');

  const handleOpenMusic = async (entry: {
    musicId?: string | null;
    musicTitle?: string | null;
    musicArtist?: string | null;
  }) => {
    // 1️⃣ If we already know the track id, go straight there
    if (entry.musicId) {
      navigate(`/musicdetails/${entry.musicId}`);
      return;
    }
  
    // 2️⃣ Fallback: search by title OR artist (not concatenated)
    const q = (entry.musicTitle || entry.musicArtist || '').trim();
    if (!q) {
      console.warn('[GalleryEditPosting.handleOpenMusic] no title/artist', entry);
      return;
    }
  
    try {
      const res = await api.get('/music', { params: { q } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
      if (!list.length) {
        console.warn('[GalleryEditPosting.handleOpenMusic] no track for', q);
        return;
      }
  
      navigate(`/musicdetails/${list[0]._id}`);
    } catch (e) {
      console.error('[GalleryEditPosting.handleOpenMusic] lookup failed:', e);
    }
  };

  
  const handleDelete = (id: string) => {
    setExistingMedia((prev) => prev.filter((item) => item.id !== id));
  };

  // Posting to Gallery (save updated media list)
  const postingRef = React.useRef(false);

  const handleSaveEdit = async () => {
    if (!postId) {
      alert(t('gallery.edit.missingPostId'));
      return;
    }
    if (postingRef.current) return;

    postingRef.current = true;
    setIsLoading(true);
    try {
      await proceedWithEdit();
    } catch (err) {
      console.error('❌ Error in handleSaveEdit:', err);
    } finally {
      postingRef.current = false;
      setIsLoading(false);
    }
  };

  const confirmSave = async () => {
  if (saveBusy) return;

  setSaveBusy(true);
  try {
    await handleSaveEdit();
  } finally {
    setSaveBusy(false);
    setSaveConfirmOpen(false);
  }
};


  const proceedWithEdit = async () => {
    if (!postId) return;
    try {
      const mediaEntries = existingMedia.map((m) =>
  m.kind === 'video'
    ? {
        isHls: true,
        videoId: m.videoId,
        musicTitle: m.musicTitle ?? null,
        musicArtist: m.musicArtist ?? null,
        musicId: m.musicId ?? null,
      }
    : {
        url: m.url,
      }
);


      const payload = {
  media: mediaEntries,
  hashtags: selectedHashtagList,
};

      await api.put(`/media/${postId}`, payload);
setSuccessOpen(true);

setTimeout(() => {
  navigate('/my-posts', { state: { reset: true } });
}, 5000);
    } catch (err) {
      console.error('❌ Failed to update post:', err);
      alert(t('gallery.edit.saveFailed'));
    }
  };
const handleCancel = () => {
  setCancelConfirmOpen(true);
};

const confirmCancel = () => {
  setCancelBusy(true);

  setUserEnteredHashtags('');
  setSelectedHashtagList([]);

  navigate('/my-posts', { replace: true });
};

const handleSuccessClose = () => {
  setSuccessOpen(false);
  navigate('/my-posts', { state: { reset: true } });
};

  const LoadingOverlay = () => (
    <div style={loadingOverlayStyle}>
      <div style={loadingContentStyle}>
        <span>{t('gallery.edit.saving')}</span>
        <div style={loadingDotsContainer}>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
        </div>
      </div>
    </div>
  );

 return (
  <>
    {isLoading && <LoadingOverlay />}

    <div style={pageShellStyle}>
      {/* HEADER — same as GalleryPosting */}
      <header style={pageHeaderStyle}>
       <div
  style={pageHeaderLeft}
  onClick={() => navigate('/gallery')}
>
  <HighLogo
    src={logo}
    style={{ width: 100, display: 'block', marginLeft: '-20px', marginTop: '6px' }}
    padding={0}
  />
</div>
          {/* TITLE — SAME POSITION AS GALLERYPOSTING */}
              <div style={postingTitleStyle}>
                ✏️ {t('gallery.edit.title', 'Change your Post')}
              </div>
      </header>

      {/* MAIN */}
      <main style={pageMainStyle}>
        <div style={cardGridStyle}>

          {/* EDIT CARD */}
          <section style={mediaOuterStyle}>
            <div style={postCardStyle}>

            

              {/* USER ROW */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '6%',
                    marginRight: 10,
                    backgroundColor: user?.picture ? 'transparent' : '#261da2',
                    backgroundImage: user?.picture ? `url(${user.picture})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: user?.picture ? 0 : 26,
                  }}
                >
                  {!user?.picture && '👻'}
                </div>

                <strong>
                  {user?.profilename || t('gallery.edit.anonymous')}
                </strong>
              </div>

              {/* 🎞 MEDIA RAIL — SAME AS GALLERYPOSTING */}
              <div style={{ ...mediaScrollerStyle, marginTop: ROW_TOP_OFFSET }}>
                {existingMedia.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'inline-block',
                      width: MEDIA_TILE_WIDTH,
                      verticalAlign: 'top',
                      marginRight:
                        index === existingMedia.length - 1 ? 0 : MEDIA_GAP,
                    }}
                  >
                    {/* VIDEO */}
                    {item.kind === 'video' ? (
                      <SmartVideoPlayerGallery
                        videoUrl={resolveVideoSrc(item)}
                        muted
                        style={{
                          maxWidth: MEDIA_TILE_WIDTH,
                          maxHeight: MEDIA_TILE_WIDTH,
                          borderRadius: 12,
                          backgroundColor: '#000',
                        }}
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={`media-${index}`}
                        loading="lazy"
                        style={{
                          maxWidth: MEDIA_TILE_WIDTH,
                          maxHeight: MEDIA_TILE_WIDTH,
                          objectFit: 'contain',
                          borderRadius: 12,
                          backgroundColor: '#000',
                        }}
                      />
                    )}

                    {/* DELETE ONLY */}
                    <div
                      style={{
                        width: '100%',
                        maxWidth: MEDIA_TILE_WIDTH,
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: 10,
                      }}
                    >
                      <button
                        onClick={() => handleDelete(item.id)}
                        style={smallActionButton}
                      >
                        {t('gallery.edit.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* ✅ BOTTOM BAR — SAME AS GALLERYPOSTING */}
              <div style={bottomBarStyle}>
                
                {/* HASHTAGS */}
                <div style={hashtagAreaStyle}>
                  <div style={hashtagCompactWrapperStyle}>
                    <HashtagSelector
                      context="gallery"
                      selectedTags={selectedHashtagList}
                      onChange={(tags) => {
                        const limited = tags.slice(0, MAX_TAGS);
                        setSelectedHashtagList(limited);
                        setUserEnteredHashtags(limited.join(' '));
                      }}
                    />
                  </div>
                </div>

                {/* SAVE / CANCEL */}
                <div style={bottomButtonGroupStyle}>
                  <button
  onClick={() => setSaveConfirmOpen(true)}
  disabled={isSaveDisabled}
  style={{
    ...postButtonStyle,
    opacity: isSaveDisabled ? 0.6 : 1,
    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
  }}
>
  🚀 {t('galleryEditSidebar.buttons.save', 'Save')}
</button>


                  <button onClick={handleCancel} style={cancelButtonStyle}>
                    ❌ {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

{saveConfirmOpen && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 700 }}>
        💾 {t('gallery.edit.confirmSaveTitle', 'Save changes to this post?')}
      </div>

      <div style={{ marginBottom: 16, fontSize: 14, color: '#9ca3af' }}>
        {t(
          'gallery.edit.confirmSaveSubtitle',
          'This will overwrite your current post.'
        )}
      </div>

      <button
        onClick={confirmSave}
        disabled={saveBusy}
        style={primaryBtnGreen}
      >
        {saveBusy
          ? t('common.saving', 'Saving...')
          : t('common.confirm', 'Yes, save')}
      </button>

      <button
        onClick={() => setSaveConfirmOpen(false)}
        disabled={saveBusy}
        style={cancelBtn}
      >
        {t('common.cancel', 'Cancel')}
      </button>
    </div>
  </div>
)}

      {/* CANCEL CONFIRM */}
      {cancelConfirmOpen && (
        <div style={confirmOverlay}>
          <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
              {t('gallery.edit.discardConfirm')}
            </div>

            <button onClick={confirmCancel} disabled={cancelBusy} style={primaryBtn}>
              {t('common.confirm')}
            </button>

            <button onClick={() => setCancelConfirmOpen(false)} disabled={cancelBusy} style={cancelBtn}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}


      {successOpen && (
  <Modal
    open={successOpen}
    onClose={handleSuccessClose}
    title={t('gallery.edit.updatedTitle', 'Post updated')}
    width={380}
  >
    <div style={{ textAlign: 'center', fontSize: 14, color: '#ddd' }}>
      ✅ {t('gallery.edit.updatedMessage', 'Your post was updated successfully.')}
    </div>

    <button
      onClick={handleSuccessClose}
      style={{
        width: '100%',
        marginTop: 14,
        padding: '10px',
        background: '#22c55e',
        border: 'none',
        borderRadius: 8,
        color: '#fff',
        cursor: 'pointer',
        fontWeight: 600,
      }}
    >
      {t('common.ok', 'OK')}
    </button>
  </Modal>
)}
    </div>
  </>
);

};

/* --- constants & styles copied / adapted from GalleryPosting --- */

const MEDIA_GAP = 14;
const MEDIA_TILE_WIDTH = Math.min(window.innerWidth - 32, 520);
const CONTENT_X_OFFSET = 0;
   
/* 🌌 Page shell */
const pageShellStyle: React.CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: 'flex',
  flexDirection: 'column',
};

/* 🧭 Header */
const pageHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0px',
  background: 'rgba(2, 6, 23, 0.1)',
  backdropFilter: 'blur(10px)',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};

/* 🧱 Main */
const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: '0px 0px 16px',
};


/* Grid: composer */
const cardGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

/* Composer area outer */
const mediaOuterStyle: React.CSSProperties = {
  width: '100%',
    flex: 1,
     marginLeft: 0,
};    
/* Composer card */
const postCardStyle: React.CSSProperties = {
  background: 'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(4, 7, 15, 0.95))',
  padding: '18px 0px 24px',
  borderRadius: 22,
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  color: '#e5e7eb',
  minHeight: 420,
};


/* Horizontal media scroller */
const mediaScrollerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: MEDIA_GAP,

  overflowX: 'scroll',
  overflowY: 'hidden',

  WebkitOverflowScrolling: 'touch',

  width: '100%',
  maxWidth: '100%',

  paddingBottom: 16,
  paddingRight: 0,

  touchAction: 'pan-x',
};


/* Small action buttons under each media */
const smallActionButton: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#020617',
  border: '1px solid #cbd5f5',
  borderRadius: 999,
  color: '#cbd5f5',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
};

/* Loading overlay */
const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const loadingContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontSize: '24px',
  color: '#380675',
};

const loadingDotsContainer: React.CSSProperties = {
  display: 'flex',
  gap: '5px',
};

const loadingDot: React.CSSProperties = {
  animation: 'loadingDots 1s infinite',
  fontSize: '36px',
};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#020617",
  borderRadius: 18,
  padding: 22,
  textAlign: "center",
  border: "1px solid rgba(148,163,184,0.35)",
  color: "#e5e7eb",
  boxShadow: "0 25px 80px rgba(0,0,0,0.9)",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "#dc2626",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "transparent",
  color: "#cbd5f5",
  cursor: "pointer",
};

const postingTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  marginTop: -8,
  letterSpacing: -0.01,
  color: '#e5e7eb',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};


const bottomBarStyle: React.CSSProperties = {
  marginTop: 20,
  paddingTop: 6,
  borderTop: '1px solid rgba(148,163,184,0.15)',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 14,  
  height: 120,
  alignItems: 'flex-start', // ✅ top aligned
  overflow: 'hidden',
};

const hashtagAreaStyle: React.CSSProperties = {
  flex: 1,
  height: '100%',
  overflowY: 'auto',
  paddingRight: 6,
  paddingTop: 2,
};


const bottomButtonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column', // ✅ vertical stack
  gap: 10,
  alignItems: 'stretch',
  flexShrink: 0,
  minWidth: 140, // keeps buttons readable on mobile
};

const postButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 999,
  background: 'linear-gradient(135deg,#22c55e,#a3e635)',
  border: 'none',
  color: '#0b1120',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.4)',
  background: 'transparent',
  color: '#e5e7eb',
  fontSize: 14,
  cursor: 'pointer',
};

const hashtagCompactWrapperStyle: React.CSSProperties = {
  marginTop: 4,
  marginBottom: 4,
};


const primaryBtnGreen: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(135deg,#22c55e,#4ade80)",
  color: "#0b1120",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

export default GalleryEditPosting;
